import {Paper, Typography, Stack, Box} from '@mui/material';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
} from 'recharts';
import WalletIcon from '$assets/svgs/wallet-con.svg?react';

import {cardStyle} from '../../styles';
import {
  chartContainerStyle,
  fieldStyle,
  titleStyle,
  percentageStyle,
  legendDotStyle,
  legendItemContainerStyle,
  legendItemContentStyle,
  tooltipContainerStyle,
  tooltipTopRowStyle,
  tooltipCountStyle,
  tooltipColorBoxStyle,
  tooltipTextStyle,
  rechartsTooltipWrapperStyle,
} from './styles';
import {DashboardData} from '$api/finance/dashboard';
import {Color} from '~/constants/style';
import {PERFERRED_PAYMENT_METHOD_CHART_CONFIG} from '../../../constant';

interface PreferredPaymentMethodsCardProps {
  data: DashboardData;
}

const PreferredPaymentMethodsCard: React.FC<
  PreferredPaymentMethodsCardProps
> = ({data}) => {
  const {
    card = {} as Partial<{count: number; percentage: number}>,
    cheque = {} as Partial<{count: number; percentage: number}>,
    cash = {} as Partial<{count: number; percentage: number}>,
    other = {} as Partial<{count: number; percentage: number}>,
  } = data.preferred_payment_methods || {};

  const paymentData = [
    {
      name: 'Credit/Debit Card',
      value: card.percentage ?? 0,
      count: card.count ?? 0,
      color: Color.Purple600,
    },
    {
      name: 'Cheque',
      value: cheque.percentage ?? 0,
      count: cheque.count ?? 0,
      color: Color.PrimaryDarkest,
    },
    {
      name: 'Cash',
      value: cash.percentage ?? 0,
      count: cash.count ?? 0,
      color: Color.PrimaryLight,
    },
    {
      name: 'Other',
      value: other.percentage ?? 0,
      count: other.count ?? 0,
      color: Color.PrimaryLightest,
    },
  ];

  const renderLegendItem = (item: (typeof paymentData)[0], index: number) => (
    <Box
      key={item.name}
      sx={{
        ...legendItemContainerStyle,
        mt: index === 0 ? 0 : 0.5,
      }}
    >
      <Box
        sx={{
          ...legendDotStyle,
          bgcolor: item.color,
        }}
      />
      <Box sx={legendItemContentStyle}>
        <Typography sx={fieldStyle}>{item.name}</Typography>
        <Typography sx={percentageStyle}>{item.value}%</Typography>
      </Box>
    </Box>
  );

  type PaymentData = {
    name: string;
    value: number;
    count: number;
    color: string;
  };

  interface CustomTooltipProps {
    active?: boolean;
    payload?: Array<{
      payload: PaymentData;
    }>;
  }

  const CustomTooltip: React.FC<CustomTooltipProps> = ({active, payload}) => {
    if (active && payload && payload.length) {
      const {name, count, color} = payload[0].payload as {
        name: string;
        count: number;
        color: string;
      };
      return (
        <Box sx={tooltipContainerStyle}>
          <Box sx={tooltipTopRowStyle}>
            <Typography sx={tooltipCountStyle}>{count}</Typography>
            <Box sx={{...tooltipColorBoxStyle, bgcolor: color}} />
          </Box>
          <Typography sx={tooltipTextStyle}>
            {`${name.charAt(0).toUpperCase() + name.slice(1).toLowerCase()} payments`}
          </Typography>
        </Box>
      );
    }
    return null;
  };

  return (
    <Paper sx={cardStyle}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <WalletIcon />
        <Typography variant="h6" sx={titleStyle}>
          Preferred payment methods
        </Typography>
      </Stack>

      <Stack direction="row" alignItems="center" spacing={1}>
        <Box sx={chartContainerStyle}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={paymentData}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                {...PERFERRED_PAYMENT_METHOD_CHART_CONFIG}
              >
                {paymentData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <RechartsTooltip
                content={<CustomTooltip />}
                wrapperStyle={rechartsTooltipWrapperStyle}
              />
            </PieChart>
          </ResponsiveContainer>
        </Box>

        <Box>{paymentData.map(renderLegendItem)}</Box>
      </Stack>
    </Paper>
  );
};

export default PreferredPaymentMethodsCard;
